import { useEffect, useMemo, useRef, useState } from "react";
import { IconPencil } from "@tabler/icons-react";
import mapboxgl from "mapbox-gl";
import AppModal from "@components/modals/AppModal";
import useLocations from "@hooks/locations/useLocations";
import env from "@configs/env";
import configureMapboxWorker from "@utils/maps/configureMapboxWorker";
import "@assets/css/locations/country-city-selector.css";

function toFiniteNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function readCoordinate(entity, keys) {
  if (!entity) return null;
  for (const key of keys) {
    const maybe = toFiniteNumber(entity[key]);
    if (maybe !== null) return maybe;
  }
  return null;
}

function CountryCitySelector({
  value,
  onChange,
  onLocationPreview,
  onClearLocationPreview,
}) {
  const {
    countries,
    cities,
    loadingCountries,
    loadingCities,
    submittingCity,
    error,
    fetchCitiesByCountry,
    createCity,
  } = useLocations();
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const mapContainerRef = useRef(null);
  const [isCityModalOpen, setIsCityModalOpen] = useState(false);
  const [searchCity, setSearchCity] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchingCity, setSearchingCity] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [mapError, setMapError] = useState("");
  const [cityForm, setCityForm] = useState({
    name: "",
    officialName: "",
    population: "",
    isCapital: "N",
    latitude: "",
    longitude: "",
    timezone: "",
  });

  const countryId = value?.countryId || "";
  const cityId = value?.cityId || "";
  const selectedCountry = useMemo(
    () => countries.find((country) => String(country.id) === String(countryId)),
    [countries, countryId],
  );
  const selectedCity = useMemo(
    () => cities.find((city) => String(city.id) === String(cityId)),
    [cities, cityId],
  );

  useEffect(() => {
    if (!countryId) return;
    fetchCitiesByCountry(countryId);
  }, [countryId, fetchCitiesByCountry]);

  useEffect(() => {
    if (!countryId) {
      if (typeof onClearLocationPreview === "function")
        onClearLocationPreview();
      return;
    }

    if (selectedCity && typeof onLocationPreview === "function") {
      const cityLng = readCoordinate(selectedCity, [
        "longitude",
        "lng",
        "lon",
        "city_longitude",
      ]);
      const cityLat = readCoordinate(selectedCity, [
        "latitude",
        "lat",
        "city_latitude",
      ]);
      if (cityLng !== null && cityLat !== null) {
        onLocationPreview({
          lng: cityLng,
          lat: cityLat,
          zoom: 10,
          withMarker: true,
        });
        return;
      }
    }

    if (selectedCountry && typeof onLocationPreview === "function") {
      const countryLng = readCoordinate(selectedCountry, [
        "longitude",
        "lng",
        "lon",
        "country_longitude",
      ]);
      const countryLat = readCoordinate(selectedCountry, [
        "latitude",
        "lat",
        "country_latitude",
      ]);
      if (countryLng !== null && countryLat !== null) {
        onLocationPreview({
          lng: countryLng,
          lat: countryLat,
          zoom: 5,
          withMarker: false,
        });
      }
    }
  }, [
    countryId,
    selectedCity,
    selectedCountry,
    onLocationPreview,
    onClearLocationPreview,
  ]);

  async function handleCreateCity(event) {
    event.preventDefault();
    if (!selectedCountry) return;
    await createCity({
      name: cityForm.name.trim(),
      officialName: cityForm.officialName.trim() || cityForm.name.trim(),
      population: cityForm.population ? Number(cityForm.population) : undefined,
      isCapital: cityForm.isCapital || "N",
      latitude: Number(cityForm.latitude),
      longitude: Number(cityForm.longitude),
      countryId: String(selectedCountry.id),
      timezone: cityForm.timezone.trim() || undefined,
    });
    setIsCityModalOpen(false);
    setSearchResults([]);
    setSearchCity("");
    setCityForm({
      name: "",
      officialName: "",
      population: "",
      isCapital: "N",
      latitude: "",
      longitude: "",
      timezone: "",
    });
  }

  useEffect(() => {
    if (
      !isCityModalOpen ||
      mapRef.current ||
      !process.env.REACT_APP_MAPBOX_TOKEN
    )
      return;
    setMapError("");

    let isCancelled = false;
    let retryId = null;

    const mountMapWhenContainerIsReady = (attempt = 0) => {
      if (isCancelled) return;
      const container = mapContainerRef.current;
      if (!container) {
        retryId = window.setTimeout(
          () => mountMapWhenContainerIsReady(attempt + 1),
          80,
        );
        return;
      }

      const { clientWidth, clientHeight } = container;
      if ((clientWidth === 0 || clientHeight === 0) && attempt < 12) {
        retryId = window.setTimeout(
          () => mountMapWhenContainerIsReady(attempt + 1),
          80,
        );
        return;
      }

      try {
        configureMapboxWorker();
        mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;
        mapRef.current = new mapboxgl.Map({
          container,
          style:
            process.env.REACT_APP_MAPBOX_STYLE ||
            "mapbox://styles/mapbox/streets-v12",
          center: [8.2275, 46.8182],
          zoom: 5,
        });
      } catch (err) {
        setMapError("Could not initialize the map in the modal.");
        return;
      }

      mapRef.current.addControl(new mapboxgl.NavigationControl(), "top-right");
      mapRef.current.once("load", () => {
        mapRef.current?.resize();
      });
      mapRef.current.on("error", () => {
        setMapError("Mapbox could not load the map. Check token/style.");
      });
      mapRef.current.on("click", (event) => {
        const { lng, lat } = event.lngLat;
        setCityForm((prev) => ({
          ...prev,
          latitude: String(lat),
          longitude: String(lng),
        }));
        if (!markerRef.current) {
          markerRef.current = new mapboxgl.Marker({ draggable: true })
            .setLngLat([lng, lat])
            .addTo(mapRef.current);
          markerRef.current.on("dragend", () => {
            const pos = markerRef.current.getLngLat();
            setCityForm((prev) => ({
              ...prev,
              latitude: String(pos.lat),
              longitude: String(pos.lng),
            }));
          });
        } else {
          markerRef.current.setLngLat([lng, lat]);
        }
      });
    };

    mountMapWhenContainerIsReady();

    return () => {
      isCancelled = true;
      if (retryId) window.clearTimeout(retryId);
    };
  }, [
    process.env.REACT_APP_MAPBOX_STYLE,
    process.env.REACT_APP_MAPBOX_TOKEN,
    isCityModalOpen,
  ]);

  useEffect(() => {
    if (!isCityModalOpen || !mapRef.current) return;
    const timeoutId = window.setTimeout(() => {
      mapRef.current?.resize();
    }, 180);
    return () => window.clearTimeout(timeoutId);
  }, [isCityModalOpen, searchResults.length]);

  useEffect(() => {
    if (isCityModalOpen) return;
    if (markerRef.current) {
      markerRef.current.remove();
      markerRef.current = null;
    }
    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
    }
  }, [isCityModalOpen]);

  async function handleSearchCity() {
    if (!searchCity.trim()) return;
    if (!process.env.REACT_APP_MAPBOX_TOKEN) {
      setSearchError("Mapbox token is missing for city search.");
      return;
    }
    setSearchError("");
    setSearchingCity(true);
    try {
      const endpoint = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(searchCity)}.json`;
      const response = await fetch(
        `${endpoint}?types=place&limit=8&language=en&access_token=${encodeURIComponent(process.env.REACT_APP_MAPBOX_TOKEN)}`,
      );
      const data = await response.json();
      setSearchResults(Array.isArray(data.features) ? data.features : []);
    } catch (err) {
      setSearchError("Could not search the city on the map.");
      setSearchResults([]);
    } finally {
      setSearchingCity(false);
    }
  }

  function applySearchResult(feature) {
    const [lng, lat] = feature.center || [];
    setCityForm((prev) => ({
      ...prev,
      name: feature.text || prev.name,
      officialName: feature.place_name || feature.text || prev.officialName,
      latitude: lat !== undefined ? String(lat) : prev.latitude,
      longitude: lng !== undefined ? String(lng) : prev.longitude,
    }));

    if (mapRef.current && lng !== undefined && lat !== undefined) {
      mapRef.current.flyTo({ center: [lng, lat], zoom: 10 });
      if (!markerRef.current) {
        markerRef.current = new mapboxgl.Marker({ draggable: true })
          .setLngLat([lng, lat])
          .addTo(mapRef.current);
      } else {
        markerRef.current.setLngLat([lng, lat]);
      }
    }
  }

  return (
    <div className="country-city-selector">
      <div className="country-city-grid">
        <label className="field-label app-form-field">
          <span className="label-inline">country</span>
          <select
            className="field-input select-control"
            value={countryId}
            onChange={(event) => {
              const nextCountryId = event.target.value;
              const nextCountry = countries.find(
                (country) => String(country.id) === String(nextCountryId),
              );
              onChange({
                countryId: nextCountryId,
                cityId: "",
                countryName: nextCountry?.name || "",
                cityName: "",
              });
            }}
          >
            <option value="">
              {loadingCountries ? "Loading countries..." : "Select country"}
            </option>
            {countries.map((country) => (
              <option key={country.id} value={country.id}>
                {country.name}
              </option>
            ))}
          </select>
        </label>

        <label className="field-label app-form-field country-city-field-with-action">
          <span className="label-inline">city</span>
          <button
            className="app-icon-btn app-icon-btn--update country-city-add-btn"
            type="button"
            disabled={!countryId}
            onClick={() => setIsCityModalOpen(true)}
            aria-label="Add city"
            title="Add city"
          >
            <IconPencil size={16} />
          </button>
          <select
            className="field-input select-control"
            value={cityId}
            onChange={(event) => {
              const nextCityId = event.target.value;
              const nextCity = cities.find(
                (city) => String(city.id) === String(nextCityId),
              );
              onChange({
                countryId,
                cityId: nextCityId,
                countryName: selectedCountry?.name || "",
                cityName: nextCity?.name || "",
              });
            }}
            disabled={!countryId}
          >
            <option value="">
              {!countryId
                ? "Select country first"
                : loadingCities
                  ? "Loading cities..."
                  : "Select city"}
            </option>
            {cities.map((city) => (
              <option key={city.id} value={city.id}>
                {city.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error ? <p className="message-error">{error}</p> : null}

      <AppModal
        isOpen={isCityModalOpen}
        onClose={() => setIsCityModalOpen(false)}
        title="Create city"
        size="lg"
      >
        <div className="country-city-search">
          <input
            className="field-input"
            placeholder="Search city on map..."
            value={searchCity}
            onChange={(event) => setSearchCity(event.target.value)}
          />
          <button
            className="button-ghost"
            type="button"
            onClick={handleSearchCity}
            disabled={searchingCity}
          >
            {searchingCity ? "Searching..." : "Search"}
          </button>
        </div>
        {searchError ? <p className="message-error">{searchError}</p> : null}
        {mapError ? <p className="message-error">{mapError}</p> : null}
        {searchResults.length ? (
          <div className="country-city-search-results">
            {searchResults.map((feature) => (
              <button
                key={feature.id}
                type="button"
                className="country-city-search-item"
                onClick={() => applySearchResult(feature)}
              >
                {feature.place_name}
              </button>
            ))}
          </div>
        ) : null}
        {!process.env.REACT_APP_MAPBOX_TOKEN ? (
          <p className="message-error">
            Cannot show map: missing Mapbox token.
          </p>
        ) : null}
        <div ref={mapContainerRef} className="country-city-map" />

        <form className="country-city-form" onSubmit={handleCreateCity}>
          <input
            className="field-input"
            placeholder="name"
            value={cityForm.name}
            onChange={(event) =>
              setCityForm((prev) => ({ ...prev, name: event.target.value }))
            }
            required
          />
          <input
            className="field-input"
            placeholder="officialName (optional)"
            value={cityForm.officialName}
            onChange={(event) =>
              setCityForm((prev) => ({
                ...prev,
                officialName: event.target.value,
              }))
            }
          />
          <input
            className="field-input"
            type="number"
            placeholder="population (optional)"
            value={cityForm.population}
            onChange={(event) =>
              setCityForm((prev) => ({
                ...prev,
                population: event.target.value,
              }))
            }
          />
          <div className="country-city-grid">
            <input
              className="field-input"
              type="number"
              step="any"
              placeholder="latitude"
              value={cityForm.latitude}
              onChange={(event) =>
                setCityForm((prev) => ({
                  ...prev,
                  latitude: event.target.value,
                }))
              }
              required
            />
            <input
              className="field-input"
              type="number"
              step="any"
              placeholder="longitude"
              value={cityForm.longitude}
              onChange={(event) =>
                setCityForm((prev) => ({
                  ...prev,
                  longitude: event.target.value,
                }))
              }
              required
            />
          </div>
          <div className="country-city-grid">
            <select
              className="field-input select-control"
              value={cityForm.isCapital}
              onChange={(event) =>
                setCityForm((prev) => ({
                  ...prev,
                  isCapital: event.target.value,
                }))
              }
            >
              <option value="N">Not a capital</option>
              <option value="Y">Capital</option>
            </select>
            <input
              className="field-input"
              placeholder="timezone (optional)"
              value={cityForm.timezone}
              onChange={(event) =>
                setCityForm((prev) => ({
                  ...prev,
                  timezone: event.target.value,
                }))
              }
            />
          </div>
          <div className="country-city-form-actions">
            <button
              className="button-primary"
              type="submit"
              disabled={submittingCity}
            >
              {submittingCity ? "Saving..." : "Save city"}
            </button>
            <button
              className="button-ghost"
              type="button"
              onClick={() => setIsCityModalOpen(false)}
            >
              Cancel
            </button>
          </div>
        </form>
      </AppModal>
    </div>
  );
}

export default CountryCitySelector;
